import { useContext, useEffect, useState } from "react";

// context
import { AdminDashboardContext } from "../../../context/AdminDashboardContext.js";

// external libraries
import { addDays, parseISO } from "date-fns";
import hr from "date-fns/locale/hr"; // croatian
import DatePicker, { registerLocale } from "react-datepicker";

// style
import Style from "./CalendarInputAdvanced.module.css";

// external styles
import "react-datepicker/dist/react-datepicker.css";

// register croatian locale
registerLocale("hr", hr);

const CalendarInputAdvanced = ({
  label,
  setSpecificState,
  initialDate,
  name,
  workingDaysLimit,
  isAdminDashboard,
  projectUpdateName,
  isSecondInputForm,
  isInFirstInput = false,
  isError,
}) => {
  const [placeholderText, setPlaceholderText] = useState("mm/dd/yyyy");

  const [selectedDate, setSelectedDate] = useState(null);

  const { projectEditable, setUpdateProjectData } = useContext(
    AdminDashboardContext
  );

  useEffect(() => {
    // Check sessionStorage for existing value
    const storedDate = sessionStorage.getItem(name);

    // Check if initialDate is provided and parse it
    if (storedDate) {
      const parsedDate = new Date(storedDate);
      setSelectedDate(parsedDate);
    }
    if (!initialDate) return;
    const parsedDate = parseISO(initialDate);

    setSelectedDate(parsedDate);
  }, [initialDate, name]);

  useEffect(() => {
    if (isAdminDashboard) {
      if (!initialDate) return;
      const parsedDate = parseISO(initialDate);
      setSelectedDate(parsedDate);
      setSpecificState(parsedDate);
    } else if (isSecondInputForm) {
      setSelectedDate(initialDate);
      setSpecificState(initialDate);
    }
  }, [initialDate, isAdminDashboard, isSecondInputForm, setSpecificState]);

  useEffect(() => {
    if (!selectedDate) {
      setPlaceholderText("dd/mm/yyyy");
    } else {
      setPlaceholderText("");
    }
    if (!isAdminDashboard) {
      setSpecificState(selectedDate);
    }
  }, [selectedDate, isAdminDashboard, setSpecificState]);

  // update sessionStorage when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      sessionStorage.setItem(name, selectedDate);
    } else {
      sessionStorage.removeItem(name);
    }
  }, [selectedDate, name]);

  // restrictions for date picker
  const isWorkingDay = (date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6; // 0 is Sunday, 6 is Saturday
  };

  // Calculate the minimum selectable date
  const minSelectableDate = (() => {
    let currentDate = new Date();
    let workingDaysCount = 0;
    // while sum of wokring days is less than [workingDaysLimit]
    while (workingDaysCount < workingDaysLimit) {
      currentDate = addDays(currentDate, 1);
      if (isWorkingDay(currentDate)) {
        workingDaysCount++;
      }
    }
    return currentDate;
  })();

  // Function to filter selectable dates
  const isDateSelectable = (date) => {
    // Check if the date is a working day and is at least 7 working days from today
    return date >= minSelectableDate;
  };

  // handle data change
  const handleChange = (date) => {
    setSelectedDate(date);
    if (isAdminDashboard) {
      const formatDate = new Date(date).toISOString();
      setUpdateProjectData((prevState) => ({
        ...prevState,
        [projectUpdateName]: formatDate,
      }));
    }
  };

  return (
    <div
      className={`${Style.DateContainer} ${
        isInFirstInput ? Style.SecondInput : ""
      }`}
    >
      <label className={Style.DateLabel}>{label}</label>
      {isError && <p className={Style.ErrorText}>Ovo polje je obavezno</p>}
      <DatePicker
        className={`${Style.DatePicker} ${isError ? Style.Error : ""}`}
        showYearDropdown={true}
        selected={selectedDate || initialDate}
        onChange={(date) => handleChange(date)}
        locale="hr"
        dateFormat="dd.MM.yyyy"
        placeholderText={placeholderText}
        filterDate={isDateSelectable}
        disabled={isAdminDashboard ? !projectEditable : false}
      />
    </div>
  );
};

export default CalendarInputAdvanced;
